import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterLink } from '@angular/router';
import { catchError, of, switchMap } from 'rxjs';

import { CONFIG } from '../../../config';
import { AuthenticationService } from '../../shared/authentication/authentication.service';
import { UserCompetitionCategoriesService } from 'src/app/shared/competition-resource/user-competition-categories.service';
import { ErrorReporterService } from 'src/app/shared/error-reporter/error-reporter.service';
import { InvoiceResourceService } from 'src/app/shared/invoice-resource/invoice-resource.service';
import { UserCerfaResourceService } from 'src/app/shared/user-cerfa-resource/user-cerfa-resource.service';

@Component({
  selector: 'app-toolbar',
  template: `<mat-toolbar color="primary">
    <button mat-icon-button class="menu" [matMenuTriggerFor]="mainMenu">
      <mat-icon>menu</mat-icon>
    </button>
    <span class="title"
      ><a routerLink="/">{{ title }}</a></span
    >
    @if (isAuthenticated()) {
      <a mat-button class="toolbar-button" [routerLink]="['/presubscriptions']">
        <mat-icon>receipt_long</mat-icon>
        Mes demandes
      </a>
      @if (hasInvoiceAccess()) {
        <a mat-button class="toolbar-button" [routerLink]="['/invoices']">
          <mat-icon>receipt</mat-icon>
          Mes factures
        </a>
      }
      @if (hasCerfaAccess().has_access) {
        <a
          mat-button
          class="toolbar-button"
          [routerLink]="['/mes-recus-fiscaux']"
        >
          <mat-icon>description</mat-icon>
          Mes reçus fiscaux
        </a>
      }
    }

    <a
      mat-button
      class="toolbar-button"
      [routerLink]="['/competitions']"
      [queryParams]="competitionQueryParams()"
    >
      <mat-icon>emoji_events</mat-icon>
      Mes compétitions
    </a>

    <span class="fill-remaining-space"></span>

    @if (canReadAdmin()) {
      <a mat-button class="toolbar-button" [routerLink]="['/admin']">
        <mat-icon>shield_person</mat-icon>
        Admin
      </a>
    }
    @if (isAnonymous()) {
      <a
        mat-button
        [routerLink]="['/user', 'login']"
        class="toolbar-button"
        (click)="onSignInClick()"
      >
        <mat-icon>login</mat-icon>
        Connexion
      </a>
    }

    @if (isAuthenticated()) {
      <button mat-button [matMenuTriggerFor]="userMenu" class="toolbar-button">
        <mat-icon>person</mat-icon>
        {{ user()?.first_name }} {{ user()?.last_name }}
        <mat-icon iconPositionEnd>keyboard_arrow_down</mat-icon>
      </button>
    }

    <mat-menu #userMenu="matMenu">
      @if (isAuthenticated()) {
        <a mat-menu-item [routerLink]="['/user', 'profile']">
          <mat-icon>account_circle</mat-icon>
          Mon profil
        </a>
        <mat-divider></mat-divider>
      }
      <button mat-menu-item (click)="onSignOutClick()">
        <mat-icon>logout</mat-icon>
        Déconnexion
      </button>
    </mat-menu>

    <mat-menu #mainMenu="matMenu">
      @if (isAuthenticated()) {
        <a mat-menu-item [routerLink]="['/presubscriptions']">
          <mat-icon>receipt_long</mat-icon>
          Mes demandes
        </a>
        @if (hasInvoiceAccess()) {
          <a mat-menu-item [routerLink]="['/invoices']">
            <mat-icon>receipt</mat-icon>
            Mes factures
          </a>
        }
        @if (hasCerfaAccess().has_access) {
          <a mat-menu-item [routerLink]="['/mes-recus-fiscaux']">
            <mat-icon>description</mat-icon>
            Mes reçus fiscaux
          </a>
        }
        <a
          mat-menu-item
          [routerLink]="['/competitions']"
          [queryParams]="competitionQueryParams()"
        >
          <mat-icon>emoji_events</mat-icon>
          Mes compétitions
        </a>
      }

      @if (isAnonymous()) {
        <a
          mat-menu-item
          [routerLink]="['/competitions']"
          [queryParams]="competitionQueryParams()"
        >
          <mat-icon>emoji_events</mat-icon>
          Compétitions
        </a>
      }

      @if (canAccessInstructorSearch()) {
        <mat-divider></mat-divider>
        <a mat-menu-item [routerLink]="['admin', 'instructor-search']">
          <mat-icon>badge</mat-icon>
          <span>Recherche licence</span>
        </a>
      }

      @if (canReadAdmin()) {
        @if (!canAccessInstructorSearch() && isAuthenticated()) {
          <mat-divider></mat-divider>
        }
        <a mat-menu-item [routerLink]="['admin', 'seasons']">
          <mat-icon>date_range</mat-icon>
          <span>Saisons</span>
        </a>
        <a mat-menu-item [routerLink]="['admin', 'seasons']">
          <mat-icon>sell</mat-icon>
          <span>Tarifs</span>
        </a>
        <a mat-menu-item [routerLink]="['admin', 'courses']">
          <mat-icon>event</mat-icon>
          <span>Cours</span>
        </a>
        <a mat-menu-item [routerLink]="['admin', 'repartition']">
          <mat-icon>group_add</mat-icon>
          <span>Répartition</span>
        </a>
        <a mat-menu-item [routerLink]="['admin', 'presubscriptions']">
          <mat-icon>group</mat-icon>
          <span>Pré-inscriptions</span>
        </a>
        <a mat-menu-item [routerLink]="['admin', 'check']">
          <mat-icon>fact_check</mat-icon>
          <span>Pointage</span>
        </a>
      }
      @if (isCompetitionManager()) {
        <a mat-menu-item [routerLink]="['admin', 'competitions']">
          <mat-icon style="font-variation-settings: 'FILL' 1"
            >emoji_events</mat-icon
          >
          <span>Compétitions</span>
        </a>
      }
      @if (canReadAdmin()) {
        <a mat-menu-item [routerLink]="['admin', 'ffme-licenses']">
          <mat-icon>card_membership</mat-icon>
          <span>Licences FFME</span>
        </a>
        <a mat-menu-item [routerLink]="['admin', 'invoices']">
          <mat-icon>receipt</mat-icon>
          <span>Factures</span>
        </a>
        <a mat-menu-item [routerLink]="['admin', 'cerfas']">
          <mat-icon>receipt_long</mat-icon>
          <span>Reçus fiscaux</span>
        </a>
        <a mat-menu-item [routerLink]="['admin', 'stats']">
          <mat-icon>query_stats</mat-icon>
          <span>Statistiques</span>
        </a>
      }

      @if (isAdmin()) {
        <mat-divider></mat-divider>
        <a mat-menu-item [routerLink]="['admin', 'google-groups']">
          <mat-icon>mail</mat-icon>
          <span>Listes de diffusion</span>
        </a>
        <a mat-menu-item [routerLink]="['admin', 'users']">
          <mat-icon>manage_accounts</mat-icon>
          <span>Utilisateur·rice·s</span>
        </a>
        <a mat-menu-item [routerLink]="['admin', 'processings']">
          <mat-icon>engineering</mat-icon>
          <span>Traitements</span>
        </a>
        <a mat-menu-item [routerLink]="['admin', 'settings']">
          <mat-icon>settings</mat-icon>
          <span>Configuration</span>
        </a>
      }

      @if (isAuthenticated()) {
        <mat-divider></mat-divider>
      }

      @if (isAnonymous()) {
        <a
          mat-menu-item
          [routerLink]="['/user', 'login']"
          (click)="onSignInClick()"
        >
          Connexion
        </a>
      }

      @if (isAuthenticated()) {
        <button mat-menu-item (click)="onSignOutClick()">Déconnexion</button>
      }
    </mat-menu>
  </mat-toolbar>`,
  styleUrls: ['./toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatToolbarModule,
    MatButtonModule,
    RouterLink,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
  ],
  providers: [InvoiceResourceService, UserCerfaResourceService],
})
export class ToolbarComponent {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly errorReporter = inject(ErrorReporterService);
  private readonly invoiceResourceService = inject(InvoiceResourceService);
  private readonly cerfaResourceService = inject(UserCerfaResourceService);
  private readonly userCategoryService = inject(
    UserCompetitionCategoriesService,
  );

  public readonly competitionQueryParams = this.userCategoryService.queryParams;

  public title: string = CONFIG.app.name;

  // Use signals from AuthenticationService to avoid ExpressionChangedAfterItHasBeenCheckedError
  public readonly isAuthenticated =
    this.authenticationService.isAuthenticatedSignal;
  public readonly isAnonymous = computed(() => !this.isAuthenticated());
  public readonly canReadAdmin = this.authenticationService.canReadAdminSignal;
  public readonly canAccessInstructorSearch =
    this.authenticationService.canAccessInstructorSearchSignal;
  public readonly isAdmin = this.authenticationService.isAdminSignal;
  public readonly isCompetitionManager =
    this.authenticationService.isCompetitionManagerSignal;
  public readonly user = this.authenticationService.userSignal;

  /**
   * Check invoice access reactively when authentication state changes.
   * Uses toObservable + switchMap pattern to:
   * - Cancel pending requests when auth state changes
   * - Automatically clean up subscriptions on component destroy
   * - Avoid the allowSignalWrites anti-pattern
   */
  public readonly hasInvoiceAccess = toSignal(
    toObservable(this.isAuthenticated).pipe(
      switchMap((isAuth) =>
        isAuth
          ? this.invoiceResourceService
              .hasAccess()
              .pipe(catchError(() => of(false)))
          : of(false),
      ),
    ),
    { initialValue: false },
  );

  /**
   * Check CERFA access reactively when authentication state changes.
   * Uses the same pattern as hasInvoiceAccess.
   */
  public readonly hasCerfaAccess = toSignal(
    toObservable(this.isAuthenticated).pipe(
      switchMap((isAuth) =>
        isAuth
          ? this.cerfaResourceService
              .hasAccess()
              .pipe(catchError(() => of({ has_access: false })))
          : of({ has_access: false }),
      ),
    ),
    { initialValue: { has_access: false } },
  );

  public onSignInClick() {
    this.authenticationService.setUrlAfterLogin();
  }

  public onSignOutClick() {
    this.authenticationService.logout();
    this.router.navigate(['/']).catch((e: Error) => {
      this.errorReporter.handleError(e);
    });
  }
}
